/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import {
  Button,
  Dialog,
  InlineNotification,
  PasswordField,
  TextField,
  Typography2,
  VisuallyHidden,
} from '@coursera/cds-core';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import Separator from 'bundles/authentication-common/components/Separator';
import * as actions from 'bundles/authentication-common/components/actions';
import {
  ARKOSE_SIGNUP_KEY,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
} from 'bundles/authentication-common/constants/botManager';
import {
  EMAIL_CONSTRAINT as isEmailValid,
  FIRSTNAME_CONSTRAINT as isFirstNameValid,
  FULLNAME_CONSTRAINT as isFullNameValid,
  LASTNAME_CONSTRAINT as isLastNameValid,
  PASSWORD_CONSTRAINT as isPasswordValid,
} from 'bundles/authentication-common/constants/constraints';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import { REGISTRATION_CONTEXT } from 'bundles/authentication-common/types';
import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import { EoiSSO } from 'bundles/expression-of-interest/components/authentication/EoiSSO';
import InternationalPhoneNumberInput from 'bundles/expression-of-interest/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { InternationalPhoneNumber } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  DEFAULT_REGION,
  newCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import {
  isValidInternationalPhoneNumber,
  toRegionFromCountryCode,
} from 'bundles/expression-of-interest/components/i18n-phone-number-input/utils';
import { SECTION_EVENT_MAP } from 'bundles/expression-of-interest/constants';
import { useAriaAnnouncement } from 'bundles/expression-of-interest/hooks/useAriaAnnouncement';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { getDefaultError } from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  handleFirstNameChange: (firstName: string) => void;
  handleLastNameChange: (lastName: string) => void;
  handleEmailChange: (email: string) => void;
  handlePhoneChange: (phone: ProfilePhone) => void;
  firstName: string;
  lastName: string;
  learnerEmail: string;
  phone: Phone;
  handleSubmit: () => Promise<void | {}>;
  navigateLogin: () => void;
  navigateSelectSubject: () => void;
  navigateConfirmation: () => void;
  trackingProductId: string;
  setIsNewSignup: (userSignedUp: boolean) => void;
  shouldSetAutoFocus?: boolean;
  eoiButtonSource?: EOIButtonSource;
  setRegion?: (region: string) => void;
};

const styles = {
  textField: css`
    margin-bottom: var(--cds-spacing-150);

    label {
      margin: 0;
    }
  `,

  divider: css`
    margin-bottom: var(--cds-spacing-150);
    margin-top: var(--cds-spacing-150);
  `,

  supportText: css`
    margin-top: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-150);
  `,
  switchToLogin: css`
    display: flex;
    margin: auto;
  `,
  reCAPTCHA: css`
    display: none;
  `,
};

export const InitialSection = (props: Props): React.ReactElement => {
  const {
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneChange,
    firstName,
    lastName,
    learnerEmail,
    phone,
    handleSubmit,
    navigateLogin,
    navigateSelectSubject,
    navigateConfirmation,
    trackingProductId,
    setIsNewSignup,
    shouldSetAutoFocus,
    eoiButtonSource,
    setRegion,
  } = props;

  const baseTrackingData = {
    eoiSection: SECTION_EVENT_MAP.initialSection,
    eoiButtonSource,
  };

  const { productType } = useEoiOptimizationsContext();

  const isLoggedOut = !isAuthenticatedUser();

  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [hasPhoneError, setHasPhoneError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | React.ReactElement>('');
  const [isRunning, setIsRunning] = useState(false);
  const { announcement: ariaAnnouncement, announce } = useAriaAnnouncement();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      publicKey: ARKOSE_SIGNUP_KEY,
    },
    reCAPTCHA: {
      container: SIGNUP_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.signup },
    },
  });

  const { trackSubmitEoi, trackInteractEoi } = useEoiEventing(trackingProductId);

  // Field validations
  useEffect(() => {
    const error = isFirstNameValid(firstName);
    if (error) {
      setFirstNameError(error);
    } else {
      setFirstNameError('');
    }
  }, [firstName]);

  useEffect(() => {
    const error = isLastNameValid(lastName);
    if (error) {
      setLastNameError(error);
    } else {
      setLastNameError('');
    }
  }, [lastName]);

  useEffect(() => {
    const error = isEmailValid(learnerEmail);
    if (error) {
      setEmailError(error);
    } else {
      setEmailError('');
    }
  }, [learnerEmail]);

  useEffect(() => {
    const error = isPasswordValid(password);

    if (error) {
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  }, [password]);

  useEffect(() => {
    if (phone.phoneNumber === '') {
      setHasPhoneError(true);
    } else {
      setHasPhoneError(false);
    }
  }, [phone]);

  /**
   * Gets the region from the phone's country code, or returns the default region.
   */
  const getRegionFromPhone = () => {
    if (phone.countryCode) {
      const countryCode = newCountryCode(phone.countryCode);
      if (countryCode) {
        return toRegionFromCountryCode(countryCode) ?? DEFAULT_REGION;
      }
    }
    return DEFAULT_REGION;
  };

  /**
   * Checks if the phone number is invalid.
   * This checks both the hasPhoneError state (set by phone component callback)
   * and also validates directly for cases where the callback hasn't fired yet.
   * Uses isValidInternationalPhoneNumber for consistent validation with the phone component.
   */
  const isPhoneInvalid = () => {
    if (hasPhoneError) return true;
    // Use the same validation as the phone component for consistency
    const region = getRegionFromPhone();
    return !isValidInternationalPhoneNumber(phone.phoneNumber as InternationalPhoneNumber, region, false);
  };

  /**
   * Collects all current validation errors and announces them to screen readers.
   *
   * @see WCAG 4.1.3 Status Messages
   */
  const collectAndAnnounceErrors = () => {
    const errors = [
      firstNameError,
      lastNameError,
      emailError,
      passwordError,
      isPhoneInvalid() ? _t('Invalid phone number') : null,
    ].filter((e): e is string => Boolean(e));
    if (errors.length > 0) {
      const errorList = errors.join('. ');
      announce(_t('Validation errors: #{errorList}', { errorList }));
    }
  };

  // Announce any existing validation errors when the Sign Up button receives focus
  const handleSignUpButtonFocus = () => {
    collectAndAnnounceErrors();
  };

  const navigateNext = () => {
    if (productType === ProductTypes.DEGREE_HUB) {
      navigateSelectSubject();
    } else {
      navigateConfirmation();
    }
  };
  // Create a Coursera account and submit EOI
  const handleCreateAccount = async () => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    const fullName = `${firstName?.trim()} ${lastName?.trim()}`;
    const fullNameError = isFullNameValid(fullName);

    if (firstNameError || lastNameError || emailError || passwordError || hasPhoneError) {
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
      collectAndAnnounceErrors();
      return setHasFormError(true);
    }
    if (fullNameError) {
      setSubmitError(fullNameError);
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
      return setHasFormError(true);
    }

    const name = `${firstName?.trim()} ${lastName?.trim()}`;

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      return setSubmitError(getDefaultError());
    }

    setIsRunning(true);

    try {
      // API call to create a new account
      await actions.signup({
        email: learnerEmail,
        name,
        password,
        registrationContext: REGISTRATION_CONTEXT.EOI,
        ...tokenData,
      });
      trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'signup' });
      setIsNewSignup(true);

      // Submit the EOI
      handleSubmit();
      navigateNext();
      Instrumentation.register();
      return trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'success' });
    } catch (error) {
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'api_failure' });

      resetBotManager();

      if (error.responseJSON.errorCode === 'existingCourseraAccount') {
        setSubmitError(_t('This email is already associated with an account. Log in or use a different email.'));
      } else {
        setSubmitError(_t("Make sure all the information you've submitted is correct"));
      }
      setIsRunning(false);
    }
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  const disclaimerText = _t(
    'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities. '
  );

  const isDegree = productType === ProductTypes.DEGREE;
  const headingText = isDegree ? _t('Learn more about this program') : _t('Learn more');

  return (
    <>
      <HeadingGroup
        supportText={_t(
          'Sign up or login to your account so we can email you information on upcoming enrollment dates, webinars, and program news.'
        )}
      >
        {headingText}
      </HeadingGroup>
      <Content data-testid="eoi-initial-section">
        {isLoggedOut && (
          <>
            <>
              <EoiSSO
                trackingProductId={trackingProductId}
                shouldSetAutoFocus={shouldSetAutoFocus}
                eoiButtonSource={eoiButtonSource}
              />
            </>

            <Separator text={_t('or')} />
            <Button variant="ghost" css={styles.switchToLogin} onClick={navigateLogin}>
              {_t('Log in with your Coursera account')}
            </Button>
          </>
        )}

        {submitError && (
          <InlineNotification title={_t('There was an error with your submission')} severity="error">
            {submitError}
          </InlineNotification>
        )}

        {/* Removed duplicate aria-live region since TextField validations handle announcements */}

        <div css={styles.reCAPTCHA} id={SIGNUP_REDESIGN_RECAPTCHA_ID} data-testid="recaptcha-signup" />

        <TextField
          label={_t('First Name')}
          fullWidth
          css={styles.textField}
          onChange={(e) => handleFirstNameChange(e.target.value)}
          validationStatus={hasFormError && !!firstNameError ? 'error' : undefined}
          validationLabel={firstNameError}
          value={firstName}
          placeholder={_t('First name')}
        />

        <TextField
          label={_t('Last Name')}
          fullWidth
          css={styles.textField}
          onChange={(e) => handleLastNameChange(e.target.value)}
          validationStatus={hasFormError && !!lastNameError ? 'error' : undefined}
          validationLabel={lastNameError}
          value={lastName}
          placeholder={_t('Last name')}
        />

        <TextField
          label={_t('Email')}
          type="email"
          fullWidth
          css={styles.textField}
          onChange={(e) => handleEmailChange(e.target.value)}
          validationStatus={hasFormError && !!emailError ? 'error' : undefined}
          validationLabel={emailError}
          value={learnerEmail}
          autoComplete="email"
          placeholder="name@email.com" // This matches the current global login form
        />

        <PasswordField
          label={_t('Password')}
          fullWidth
          onChange={(e) => setPassword(e.target.value)}
          validationStatus={hasFormError && !!passwordError ? 'error' : undefined}
          validationLabel={passwordError}
          value={password}
          supportText={_t('Between 8 and 72 characters')}
          css={styles.textField}
          placeholder={_t('Enter your password')}
        />

        <InternationalPhoneNumberInput
          profile={{ phone }}
          isOptional={false}
          isEmptyValid={false}
          customStyles={customFormattedPhoneStyles}
          submissionError={hasFormError}
          handleFormChange={handlePhoneChange}
          setRegion={setRegion}
          handleError={(err) => {
            setHasPhoneError(err);
            if (err) {
              setHasFormError(true);
            }
          }}
          ref={phoneNumberRef}
          ariaDescribedBy="phone-consent-initial"
        />

        <Typography2
          component="p"
          variant="bodySecondary"
          color="supportText"
          css={styles.supportText}
          id="phone-consent-initial"
        >
          {disclaimerText}
        </Typography2>
      </Content>

      <Actions>
        <Button onClick={handleCreateAccount} onFocus={handleSignUpButtonFocus} disabled={isRunning}>
          {_t('Sign Up')}
        </Button>
      </Actions>
      {/* Screen reader announcement for validation errors */}
      <VisuallyHidden>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {ariaAnnouncement}
        </div>
      </VisuallyHidden>
    </>
  );
};

export default InitialSection;
